import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";

/**
 * Lightweight compose-and-send modal. Mirrors the family-profile Email flow but
 * self-contained: it reads the branch From address (GET api/email-service/get-email)
 * and posts to api/email-service/send — the same endpoint the full compose page
 * uses. The To field is a removable-chip multi-select seeded with every parent
 * email on file; staff can drop a chip or type an arbitrary address to add one.
 */

// Split a comma-joined email list into a de-duped array of trimmed addresses.
const splitEmails = (csv) =>
  (csv || "")
    .split(",")
    .map((e) => e.trim())
    .filter((e, i, arr) => e && arr.indexOf(e) === i);

const toOption = (email) => ({ value: email, label: email });

// Parse the dashboard's `parent_contacts` string — "Name::email|Name::email"
// (primary first) — into chip options whose label shows the parent's name and
// whose value is the bare email the send endpoint expects.
const parseContacts = (contacts) =>
  (contacts || "")
    .split("|")
    .map((entry) => {
      const idx = entry.lastIndexOf("::");
      if (idx === -1) return null;
      const name = entry.slice(0, idx).trim();
      const email = entry.slice(idx + 2).trim();
      if (!email) return null;
      return { value: email, label: name ? `${name} <${email}>` : email };
    })
    .filter((o, i, arr) => o && arr.findIndex((x) => x && x.value === o.value) === i);

export function QuickEmailModal({
  show,
  onHide,
  to,
  parentEmails,
  parentContacts,
  studentName,
  branchId,
  authHeaders,
}) {
  const [fromEmail, setFromEmail] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [options, setOptions] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!show) return;
    setError("");
    setSent(false);
    setSubject("");
    setBody("");
    // Seed every parent (primary first) with name + email chips; fall back to
    // a plain email list, then the single `to`.
    const seeded = parentContacts
      ? parseContacts(parentContacts)
      : splitEmails(parentEmails || to).map(toOption);
    setRecipients(seeded);
    // Keep the seeded parents in the dropdown so a removed chip can be re-added.
    setOptions(seeded);
    if (!branchId) return;
    axios
      .get(`api/email-service/get-email?branchId=${branchId}`, authHeaders)
      .then((res) => {
        const rows = (res.data && res.data.results) || [];
        setFromEmail(rows[0] ? rows[0].email : "");
      })
      .catch(() => setFromEmail(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, branchId]);

  const send = async () => {
    const emails = recipients.map((r) => r.value).filter(Boolean);
    if (!emails.length) return setError("Add at least one recipient.");
    if (!fromEmail)
      return setError(
        "Couldn't load the branch sending address — reopen and try again."
      );
    if (!subject.trim()) return setError("Please enter a subject.");
    if (!body.trim()) return setError("Please enter a message.");
    setSending(true);
    setError("");
    try {
      await axios.post(
        "api/email-service/send",
        {
          fromEmail,
          to: emails,
          bcc: [],
          subject,
          emailBody: body.replace(/\n/g, "<br/>"),
          branchId,
        },
        authHeaders
      );
      setSent(true);
    } catch (e) {
      setError("Could not send the email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const sentTo = recipients.map((r) => r.value).join(", ");

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          Email {studentName ? `${studentName}'s parents` : "parents"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <div className="alert alert-danger">{error}</div> : ""}
        {sent ? (
          <div className="alert alert-success mb-0">Email sent to {sentTo}.</div>
        ) : (
          <Form>
            <Form.Group>
              <Form.Label>From</Form.Label>
              <Form.Control value={fromEmail} readOnly disabled />
            </Form.Group>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <CreatableSelect
                isMulti
                value={recipients}
                options={options}
                onChange={(vals) => setRecipients(vals || [])}
                placeholder="Select or add email recipients"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Subject</Form.Label>
              <Form.Control
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message…"
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={sending}>
          {sent ? "Close" : "Cancel"}
        </Button>
        {!sent && (
          <Button
            variant="primary"
            onClick={send}
            disabled={sending || !recipients.length || !fromEmail}
          >
            {sending ? <Spinner animation="border" size="sm" /> : "Send"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
